import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import "@/App.css";
import { LanguageProvider } from "@/context/LanguageContext";
import useLenis from "@/hooks/useLenis";
import SidebarNav from "@/components/SidebarNav";
import FooterV3 from "@/components/FooterV3";
import SkipLink from "@/components/SkipLink";
import useAnalytics from "@/hooks/useAnalytics";

import HomeV3        from "@/pages/HomeV3";
import IdeasPage     from "@/pages/IdeasPage";
import ResearchPage  from "@/pages/ResearchPage";
import ProjectsPage  from "@/pages/ProjectsPage";
import AboutPage     from "@/pages/AboutPage";
import PhilosophyPage from "@/pages/PhilosophyPage";
import LighthousePage from "@/pages/LighthousePage";
import ContactPage   from "@/pages/ContactPage";
import EssayReaderV3 from "@/pages/EssayReaderV3";
import MemoirePage from "@/pages/MemoirePage";
import MemoireDetailPage from "@/pages/MemoireDetailPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import AdminInboxPage from "@/pages/AdminInboxPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminContentEditPage from "@/pages/AdminContentEditPage";
import PreviewPage from "@/pages/PreviewPage";
import { LegalNoticePage, PrivacyPolicyPage, CookiePolicyPage, TermsPage, DisclaimerPage, CopyrightPage } from "@/pages/LegalPages";
import ConsentBanner from "@/components/ConsentBanner";
import useHreflang from "@/hooks/useHreflang";

function ScrollTop() {
    const { pathname, hash } = useLocation();
    React.useEffect(() => {
        if (hash) {
            // let the anchor scroll happen naturally
            const el = document.getElementById(hash.replace("#", ""));
            if (el) { el.scrollIntoView({ behavior: "auto", block: "start" }); return; }
        }
        window.scrollTo(0, 0);
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    }, [pathname, hash]);
    return null;
}

// Legacy library routes: /library/:collection/:slug or /library/:slug → route
// the reader by inferring surface. Only slug is required; fall back to /ideas.
function LibraryDocRedirect() {
    const { slug } = useParams();
    return <Navigate to={`/ideas/${slug}`} replace />;
}
function LibraryCollectionRedirect() {
    // Any subcollection → main Ideas hub (Research if it's research-notes/methods)
    const { collection } = useParams();
    const researchSet = new Set(["research-notes", "methods"]);
    return <Navigate to={researchSet.has(collection) ? "/research" : "/ideas"} replace />;
}

function Shell() {
    useLenis();
    useAnalytics();
    useHreflang();
    const { pathname } = useLocation();
    const isAdmin = pathname.startsWith("/admin");
    return (
        <>
            <SkipLink />
            {!isAdmin && <SidebarNav />}
            <ScrollTop />
            <Routes>
                <Route path="/" element={<HomeV3 />} />

                {/* V3 primary */}
                <Route path="/ideas"     element={<IdeasPage />} />
                <Route path="/ideas/:slug"    element={<EssayReaderV3 mode="ideas" />} />
                <Route path="/research"  element={<ResearchPage />} />
                <Route path="/research/:slug" element={<EssayReaderV3 mode="research" />} />
                <Route path="/projects"  element={<ProjectsPage />} />
                <Route path="/projects/:slug" element={<ProjectDetailPage />} />
                <Route path="/memoire"     element={<MemoirePage />} />
                <Route path="/memoire/:slug" element={<MemoireDetailPage />} />
                <Route path="/about"     element={<AboutPage />} />
                <Route path="/philosophy" element={<PhilosophyPage />} />
                <Route path="/lighthouse" element={<LighthousePage />} />
                <Route path="/contact"   element={<ContactPage />} />

                {/* Token-protected draft preview — unlinked, noindex */}
                <Route path="/preview/:token" element={<PreviewPage />} />

                {/* Legal */}
                <Route path="/legal-notice" element={<LegalNoticePage />} />
                <Route path="/privacy"      element={<PrivacyPolicyPage />} />
                <Route path="/cookies"      element={<CookiePolicyPage />} />
                <Route path="/disclaimer"   element={<DisclaimerPage />} />
                <Route path="/copyright"    element={<CopyrightPage />} />
                <Route path="/terms"        element={<TermsPage />} />

                {/* Private admin — unlinked, token-gated */}
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/inbox" element={<AdminInboxPage />} />
                <Route path="/admin/content" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/content/:ctype/new" element={<AdminContentEditPage />} />
                <Route path="/admin/content/:ctype/:id/edit" element={<AdminContentEditPage />} />

                {/* Redirects — preserve external links */}
                <Route path="/institute"    element={<Navigate to="/about" replace />} />
                <Route path="/framework"    element={<Navigate to="/philosophy" replace />} />
                <Route path="/constitution" element={<Navigate to="/philosophy" replace />} />
                <Route path="/legacy"       element={<Navigate to="/about#pepe" replace />} />
                <Route path="/impact"       element={<Navigate to="/projects" replace />} />
                <Route path="/library"      element={<Navigate to="/ideas" replace />} />
                <Route path="/library/:collection" element={<LibraryCollectionRedirect />} />
                <Route path="/library/:collection/:slug" element={<LibraryDocRedirect />} />
                <Route path="/thinking"     element={<Navigate to="/ideas" replace />} />
                <Route path="/thinking/:slug" element={<LibraryDocRedirect />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {!isAdmin && (
                <div className="lg:pl-[var(--sidebar-w)]">
                    <FooterV3 />
                </div>
            )}
            {!isAdmin && <ConsentBanner />}
        </>
    );
}

export default function App() {
    return (
        <div className="App">
            <LanguageProvider>
                <BrowserRouter>
                    <Shell />
                </BrowserRouter>
            </LanguageProvider>
        </div>
    );
}
